import React, { useMemo, useState } from 'react';
import { ExternalLinkIcon } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { SearchInput } from '../components/ui/SearchInput';
import { Pagination, Table, TD, TH, THead, TR } from '../components/ui/Table';
import { StudentPanel } from '../components/panels/StudentPanel';
import { schools } from '../data/district';
import { students } from '../data/students';
import { usePagination } from '../hooks/usePagination';
import { Student } from '../types';
export function StudentDirectory() {
  const [schoolQuery, setSchoolQuery] = useState('');
  const [studentQuery, setStudentQuery] = useState('');
  const [activeSchool, setActiveSchool] = useState('all');
  const [selected, setSelected] = useState<Student | null>(null);
  const schoolOptions = useMemo(() => {
    const q = schoolQuery.trim().toLowerCase();
    return [{
      slug: 'all',
      name: 'All Schools'
    }, ...schools].filter(option => q ? option.name.toLowerCase().includes(q) : true);
  }, [schoolQuery]);
  const rows = useMemo(() => {
    const q = studentQuery.trim().toLowerCase();
    return students.filter(student => {
      if (activeSchool !== 'all' && student.schoolSlug !== activeSchool) return false;
      return `${student.name} ${student.email} ${student.studentId}`.toLowerCase().includes(q);
    });
  }, [studentQuery, activeSchool]);
  const {
    page,
    setPage,
    totalPages,
    pageItems
  } = usePagination(rows, 25);
  return <>
      <PageHeader title="Student Directory" subtitle="View & manage all the students in the district" data-id="element-672" data-mp="14apj9svicup" />

      <div className="flex flex-1 flex-col bg-white px-6 py-5" data-id="element-673" data-mp="3jia39sy2g6aa">
        <div className="flex flex-1 flex-col gap-8 lg:flex-row" data-id="element-674" data-mp="3jfxg91dgb5qd">
          <aside className="w-full shrink-0 lg:w-[190px]" data-id="element-675" data-mp="3ohd04q3w7676">
            <h2 className="text-[13px] font-bold text-ink-900" data-id="element-676" data-mp="39j2i4jl3b7cm">Filter by School</h2>
            <div className="mt-3" data-id="element-677" data-mp="3einqfkm5gs20">
              <SearchInput value={schoolQuery} onChange={setSchoolQuery} placeholder="Search schools by name" data-id="element-678" data-mp="3ehm6eqn5ov85" />
            </div>
            <ul className="mt-3 space-y-0.5" data-id="element-679" data-mp="2qn27tga0a2jn">
              {schoolOptions.map(option => {
              const active = option.slug === activeSchool;
              return <li key={option.slug} data-id="element-680" data-mp="2gpo6knwnlkbx">
                    <button type="button" onClick={() => setActiveSchool(option.slug)} aria-current={active ? 'true' : undefined} className={`w-full rounded-md px-3 py-2 text-left text-[13px] transition-colors ${active ? 'bg-ink-100 font-medium text-ink-900' : 'text-ink-700 hover:bg-ink-50'}`} data-id="element-681" data-mp="2gqpjkxx8cgwa">
                      {option.name}
                    </button>
                  </li>;
            })}
            </ul>
          </aside>

          <div className="flex min-w-0 flex-1 flex-col" data-id="element-682" data-mp="1nz5s6qqhbwj7">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3" data-id="element-683" data-mp="3j7pf6v3g8sg">
              <SearchInput value={studentQuery} onChange={setStudentQuery} placeholder="Search students by name, email or device ID" className="w-[290px]" data-id="element-684" data-mp="8h9hghahzig2" />
              <button type="button" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-700 hover:text-brand-600" data-id="element-685" data-mp="i8461sb7uu4o">
                Export List
                <ExternalLinkIcon className="h-3.5 w-3.5" data-id="element-686" data-mp="r67hfvshmair" />
              </button>
            </div>

            <Table minWidth={680} data-id="element-687" data-mp="w7wtnerg4u8f">
              <THead data-id="element-688" data-mp="1166t41k7egzu">
                <tr data-id="element-689" data-mp="116ghjkplog3f">
                  <TH data-id="element-690" data-mp="114vpc1qrd98q">Student Name</TH>
                  <TH data-id="element-691" data-mp="113uadm6mg2lq">Student Email</TH>
                  <TH data-id="element-692" data-mp="2xz9zq755h862">Student ID</TH>
                </tr>
              </THead>
              <tbody data-id="element-693" data-mp="32xbdqdnpyhao">
                {pageItems.length === 0 && <TR data-id="element-694" data-mp="32znx8v94kzpu">
                    <TD colSpan={3} className="py-10 text-center text-ink-500" data-id="element-695" data-mp="x90z7cxn7p0g">
                      No students found
                    </TD>
                  </TR>}
                {pageItems.map(student => <TR key={student.id} onClick={() => setSelected(student)} data-id="element-696" data-mp="168xvo0q9xs62">
                    <TD data-id="element-697" data-mp="127uglgg1w05w">{student.name}</TD>
                    <TD data-id="element-698" data-mp="128vtzrkjgyqs">{student.email}</TD>
                    <TD data-id="element-699" data-mp="eijm2gpan207">{student.studentId}</TD>
                  </TR>)}
              </tbody>
            </Table>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} data-id="element-700" data-mp="9krk0tuxpzia" />
          </div>
        </div>
      </div>

      <StudentPanel student={selected} onClose={() => setSelected(null)} data-id="element-701" data-mp="nco6ah9x9f6r" />
    </>;
}